import { useEffect, useRef, useState, type FocusEvent, type PointerEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Pencil, Plus, Quote } from 'lucide-react';

import { useReportCreateFab } from '@/contexts/CreateFabContext';

const longPressMs = 500;

export function CreateCardFab({
  onContextual,
  onManual,
}: {
  onContextual: () => void;
  onManual: () => void;
}) {
  const [dialOpen, setDialOpen] = useState(false);
  const suppressClickRef = useRef(false);
  const pressTimerRef = useRef<number | null>(null);
  const reduceMotion = useReducedMotion();

  useReportCreateFab(dialOpen);

  const clearPressTimer = () => {
    if (pressTimerRef.current !== null) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  useEffect(() => clearPressTimer, []);

  useEffect(() => {
    if (!dialOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDialOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialOpen]);

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse') return;

    clearPressTimer();
    suppressClickRef.current = false;
    pressTimerRef.current = window.setTimeout(() => {
      setDialOpen(true);
      suppressClickRef.current = true;
    }, longPressMs);
  };

  const handleMainClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    setDialOpen(false);
    onContextual();
  };

  const handleManualClick = () => {
    setDialOpen(false);
    onManual();
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setDialOpen(false);
    }
  };

  return (
    <>
      {dialOpen && (
        <button
          type="button"
          aria-label="Dismiss create card menu"
          className="fixed inset-0 z-30 cursor-default"
          onClick={() => setDialOpen(false)}
        />
      )}

      <div
        className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-40 flex flex-col items-end gap-3 sm:right-6 md:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]"
        onMouseEnter={() => setDialOpen(true)}
        onMouseLeave={() => setDialOpen(false)}
        onFocus={() => setDialOpen(true)}
        onBlur={handleBlur}
      >
        <AnimatePresence>
          {dialOpen && (
            <motion.div
              initial={reduceMotion ? undefined : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduceMotion ? undefined : { opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="flex items-center gap-2"
            >
              <button
                type="button"
                onClick={handleManualClick}
                className="whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.14)] transition hover:bg-slate-50"
              >
                Manual
              </button>
              <button
                type="button"
                aria-label="Create card manually"
                onClick={handleManualClick}
                className="flex size-11 shrink-0 items-center justify-center rounded-full border border-white bg-white text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.14)] transition hover:bg-slate-50"
              >
                <Pencil className="size-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-2">
          <AnimatePresence>
            {dialOpen && (
              <motion.button
                type="button"
                onClick={handleMainClick}
                initial={reduceMotion ? undefined : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={reduceMotion ? undefined : { opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.14)] transition hover:bg-slate-50"
              >
                Contextual
              </motion.button>
            )}
          </AnimatePresence>

          <button
            type="button"
            aria-label="Create card"
            aria-haspopup="menu"
            aria-expanded={dialOpen}
            onPointerDown={handlePointerDown}
            onPointerUp={clearPressTimer}
            onPointerLeave={clearPressTimer}
            onPointerCancel={clearPressTimer}
            onContextMenu={(event) => event.preventDefault()}
            onClick={handleMainClick}
            style={{ WebkitTouchCallout: 'none', touchAction: 'manipulation' }}
            className="flex size-14 shrink-0 select-none items-center justify-center rounded-full bg-slate-950 text-white shadow-[0_14px_45px_rgba(15,23,42,0.25)] transition hover:bg-slate-800 active:scale-95"
          >
            <span className="relative flex size-6 items-center justify-center">
              <AnimatePresence initial={false}>
                {dialOpen ? (
                  <motion.span
                    key="quote"
                    initial={reduceMotion ? undefined : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduceMotion ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Quote className="size-6" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="plus"
                    initial={reduceMotion ? undefined : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduceMotion ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Plus className="size-6" />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
